/**
 * Admin → Payment Reconciliation
 *
 * Access via: /admin/payment-reconcile  (admin roles only)
 *
 * When a customer pays on Razorpay but the webhook silently fails (cold start,
 * mid-flight crash, delivery miss), the CRM never creates the customer or
 * inspection record. Ops opens this page, enters the payment identifier (or
 * just the customer's phone), clicks Reconcile, and the backend idempotently
 * re-runs the webhook logic.
 *
 * Backed by POST /api/admin/payments/reconcile.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle, ArrowRight,
  Phone, Hash, CreditCard, Link2, User, Receipt, Activity, Bot, UserCheck,
  Inbox, Send, Mail,
} from 'lucide-react';
import { adminApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';

const ADMIN_ROLES = ['CEO', 'CTO', 'CTOO', 'COUNTRY_HEAD', 'HR_HEAD', 'SALES_HEAD'];

const FIELDS = [
  {
    key: 'payment_id',
    label: 'Razorpay Payment ID',
    placeholder: 'pay_SgUwk3bQmqm0dY',
    icon: CreditCard,
    hint: 'From the Razorpay dashboard (preferred — most reliable).',
  },
  {
    key: 'payment_link_id',
    label: 'Payment Link ID',
    placeholder: 'plink_xxxxxxxxxxxx',
    icon: Link2,
    hint: 'When you have the link ID but not the payment ID.',
  },
  {
    key: 'lead_id',
    label: 'CRM Lead ID',
    placeholder: 'uuid',
    icon: Hash,
    hint: 'When you already know which lead to reconcile.',
  },
  {
    key: 'phone',
    label: 'Customer Phone',
    placeholder: '9611025457',
    icon: Phone,
    hint: 'Fallback — matches the most-recently-updated lead with a payment link.',
  },
];

export default function PaymentReconcile() {
  const { user } = useAuth();
  const [values, setValues] = useState({ payment_id: '', payment_link_id: '', lead_id: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Defensive route-level guard (router-level guard also exists in App.js).
  if (!user || !ADMIN_ROLES.includes(user.role_code)) {
    return <Navigate to="/dashboard" replace />;
  }

  const filledCount = FIELDS.filter(f => values[f.key].trim()).length;
  const canSubmit = filledCount > 0 && !loading;

  const handleReset = () => {
    setValues({ payment_id: '', payment_link_id: '', lead_id: '', phone: '' });
    setResult(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    setResult(null);
    setError(null);

    const body = {};
    FIELDS.forEach(f => { if (values[f.key].trim()) body[f.key] = values[f.key].trim(); });

    try {
      const res = await adminApi.reconcilePayment(body);
      setResult(res.data);
      if (res.data?.success) {
        toast.success(res.data.message || 'Reconciliation complete');
      } else {
        toast.warning(res.data?.message || 'Nothing to reconcile');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Reconciliation failed';
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4" data-testid="payment-reconcile-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <RefreshCw className="h-6 w-6 text-blue-600" />
          Payment Reconciliation
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Recover payments captured on Razorpay whose webhook did not complete — creates the
          missing customer and inspection record, idempotently.
        </p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Identify the payment</CardTitle>
          <CardDescription>
            Fill any one of the fields below. The most specific identifier wins.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {FIELDS.map(f => {
              const Icon = f.icon;
              return (
                <div key={f.key}>
                  <Label htmlFor={f.key} className="flex items-center gap-2 mb-1">
                    <Icon className="h-4 w-4 text-slate-400" />
                    {f.label}
                  </Label>
                  <Input
                    id={f.key}
                    data-testid={`reconcile-input-${f.key}`}
                    placeholder={f.placeholder}
                    value={values[f.key]}
                    onChange={e => setValues({ ...values, [f.key]: e.target.value })}
                    disabled={loading}
                    autoComplete="off"
                  />
                  <p className="text-xs text-slate-500 mt-1">{f.hint}</p>
                </div>
              );
            })}

            <div className="flex items-center gap-2 pt-2">
              <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="reconcile-submit-btn"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Reconcile Payment
              </Button>
              <Button type="button" variant="outline" onClick={handleReset} disabled={loading} data-testid="reconcile-reset-btn">
                Reset
              </Button>
              {filledCount > 1 && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Multiple fields provided — all will be sent; server picks the most specific.
                </span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mb-6" data-testid="reconcile-error">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {result && <ReconciliationReport result={result} />}

      <UnmatchedPayments />

      <ReconciliationLog />

      <Card className="mt-6 bg-slate-50 border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm text-slate-700">How this works</CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-slate-600 space-y-2">
          <p><strong>Safe to re-run.</strong> The endpoint is idempotent — running it twice never creates duplicate customers or inspections.</p>
          <p><strong>Only admins can use this page.</strong> Every reconciliation logs an activity on the lead with your name.</p>
          <p><strong>When to use:</strong> customer says "I paid" but CRM shows no inspection. Check Razorpay dashboard, grab the payment_id, paste above.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ----------------------------------------------------------------------------

function ReconciliationReport({ result }) {
  const r = result.report || {};
  const ok = !!result.success;
  const createdCount = (r.inspections_created || []).length;
  const already = !!r.already_processed;

  return (
    <Card
      className={ok ? 'border-emerald-200 bg-emerald-50/40' : 'border-amber-200 bg-amber-50/40'}
      data-testid="reconcile-result"
    >
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
          {result.message || (ok ? 'Success' : 'Partial')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <ReportRow icon={Hash} label="Resolved by" value={r.resolved_by || '—'} mono />
        <ReportRow icon={User} label="Lead" value={r.lead_id || '—'} mono />
        {r.lead_status_before && r.lead_status_after && (
          <ReportRow
            icon={ArrowRight}
            label="Lead status"
            value={
              <span>
                <span className="px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-mono text-xs">{r.lead_status_before}</span>
                <ArrowRight className="inline h-3 w-3 mx-1 text-slate-400" />
                <span className="px-2 py-0.5 rounded bg-emerald-200 text-emerald-800 font-mono text-xs">{r.lead_status_after}</span>
              </span>
            }
          />
        )}
        <ReportRow
          icon={User}
          label="Customer"
          value={
            r.customer_id ? (
              <span>
                <span className="font-mono text-xs">{r.customer_id}</span>
                {r.customer_created && <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">newly created</span>}
              </span>
            ) : '—'
          }
        />
        <ReportRow
          icon={Receipt}
          label="Inspections"
          value={
            <span>
              <span className="font-medium text-emerald-700">{createdCount} created</span>
              {r.inspections_already_present > 0 && (
                <span className="text-slate-500"> · {r.inspections_already_present} already existed</span>
              )}
              {already && createdCount === 0 && (
                <span className="ml-2 px-2 py-0.5 rounded bg-slate-200 text-slate-700 text-xs">already processed</span>
              )}
            </span>
          }
        />
        <ReportRow
          icon={CreditCard}
          label="Razorpay"
          value={
            <span>
              <span className={`px-2 py-0.5 rounded font-mono text-xs ${r.razorpay_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                {r.razorpay_status || 'unknown'}
              </span>
              {typeof r.razorpay_amount === 'number' && (
                <span className="ml-2 font-semibold">{activeCurrencySymbol()}{r.razorpay_amount.toLocaleString('en-IN')}</span>
              )}
            </span>
          }
        />
        {Array.isArray(r.warnings) && r.warnings.length > 0 && (
          <div className="pt-2 border-t border-slate-200">
            <div className="flex items-center gap-1 text-amber-700 mb-1">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium">Warnings</span>
            </div>
            <ul className="list-disc list-inside text-xs text-amber-800 space-y-1">
              {r.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
        {r.lead_id && (
          <div className="pt-2 border-t border-slate-200">
            <a
              href={`/leads?open=${r.lead_id}`}
              className="text-xs text-blue-600 hover:underline"
              data-testid="reconcile-view-lead-link"
            >
              View lead →
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReportRow({ icon: Icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className={`text-sm text-slate-900 ${mono ? 'font-mono' : ''} break-all`}>{value}</div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------

function ReconciliationLog() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.reconcileLog(50);
      setRows(res.data?.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const stats = rows ? rows.reduce((a, r) => {
    if (r.source === 'cron') a.cron += 1; else a.manual += 1;
    if (r.decision === 'healed') a.healed += 1;
    return a;
  }, { cron: 0, manual: 0, healed: 0 }) : null;

  return (
    <Card className="mt-6" data-testid="reconcile-log-widget">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4 text-slate-500" />
            Reconciliation Log
          </CardTitle>
          <CardDescription>
            Recent decisions from the 15-min auto-heal cron and manual reconciliations.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="reconcile-log-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {stats && (
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            <Stat label="Total shown" value={rows.length} />
            <Stat label="Auto-healed" value={stats.healed} accent={stats.healed > 0 ? 'emerald' : 'slate'} />
            <Stat label="Cron" value={stats.cron} />
            <Stat label="Manual" value={stats.manual} />
          </div>
        )}
        {!rows ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">
            No reconciliations yet. The auto-heal cron runs every 15 min in production.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full text-xs">
              <thead className="text-slate-500 text-left border-b border-slate-200">
                <tr>
                  <th className="px-6 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">Decision</th>
                  <th className="px-3 py-2 font-medium">Lead / Link</th>
                  <th className="px-3 py-2 font-medium text-right">Amount</th>
                  <th className="px-6 py-2 font-medium">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r, i) => (
                  <tr key={r.id || i} className="hover:bg-slate-50" data-testid={`reconcile-log-row-${i}`}>
                    <td className="px-6 py-2 text-slate-600 whitespace-nowrap">
                      {r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {r.source === 'cron' ? (
                        <span className="inline-flex items-center gap-1 text-purple-700"><Bot className="h-3 w-3" /> cron</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-blue-700"><UserCheck className="h-3 w-3" /> manual</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <DecisionBadge value={r.decision} />
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-700 max-w-[180px] truncate">
                      {r.lead_id || r.payment_link_id || '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-slate-700">
                      {typeof r.razorpay_amount === 'number' ? `${activeCurrencySymbol()}${r.razorpay_amount.toLocaleString('en-IN')}` : '—'}
                    </td>
                    <td className="px-6 py-2 text-slate-500">{r.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, accent = 'slate' }) {
  const colour = {
    slate: 'bg-slate-100 text-slate-700',
    emerald: 'bg-emerald-100 text-emerald-700',
  }[accent];
  return <span className={`px-2 py-1 rounded ${colour}`}><span className="font-semibold">{value}</span> {label}</span>;
}

function DecisionBadge({ value }) {
  const map = {
    healed: 'bg-emerald-100 text-emerald-700',
    recorded: 'bg-emerald-100 text-emerald-700',
    already_processed: 'bg-slate-100 text-slate-600',
    stale_paid: 'bg-slate-100 text-slate-600',
    no_op: 'bg-slate-100 text-slate-600',
    no_lead: 'bg-amber-100 text-amber-700',
    unmatched: 'bg-amber-100 text-amber-700',
    razorpay_error: 'bg-red-100 text-red-700',
    no_plink_id: 'bg-red-100 text-red-700',
  };
  const cls = map[value] || 'bg-slate-100 text-slate-600';
  return <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${cls}`}>{value || '—'}</span>;
}

// ----------------------------------------------------------------------------

/**
 * Unmatched Payments review queue.
 * Payments Razorpay captured but the centralized sync could NOT resolve to a
 * lead (no lead_id, no matching link, no unique mobile match). Ops pastes the
 * correct CRM lead id and clicks Map to attach + record the payment.
 */
function UnmatchedPayments() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leadIds, setLeadIds] = useState({});   // payment_id -> lead_id input
  const [leadTypes, setLeadTypes] = useState({}); // payment_id -> lead_type
  const [mapping, setMapping] = useState(null);   // payment_id currently mapping

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.unmatchedPayments('pending', 200);
      setRows(res.data?.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleMap = async (paymentId) => {
    const leadId = (leadIds[paymentId] || '').trim();
    if (!leadId) { toast.error('Enter a CRM lead id first'); return; }
    setMapping(paymentId);
    try {
      const body = { lead_id: leadId };
      if (leadTypes[paymentId]) body.lead_type = leadTypes[paymentId];
      const res = await adminApi.mapUnmatchedPayment(paymentId, body);
      toast.success(`Mapped to ${res.data?.lead_type || 'lead'} ${leadId.slice(0, 8)}…`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Mapping failed');
    } finally {
      setMapping(null);
    }
  };

  const count = rows ? rows.length : 0;

  return (
    <Card
      className={`mt-6 ${count > 0 ? 'border-amber-300 bg-amber-50/40' : ''}`}
      data-testid="unmatched-payments-widget"
    >
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Inbox className={`h-4 w-4 ${count > 0 ? 'text-amber-600' : 'text-slate-500'}`} />
            Unmatched Payments
            {count > 0 && (
              <span
                className="ml-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-semibold"
                data-testid="unmatched-payments-count"
              >
                {count}
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Razorpay payments that couldn't be auto-matched to a lead (no id / link / unique mobile).
            Paste the correct CRM lead id and map it — the payment is then recorded idempotently.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="unmatched-payments-refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {!rows ? (
          <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-emerald-700 py-4 text-center flex items-center justify-center gap-2" data-testid="unmatched-payments-empty">
            <CheckCircle2 className="h-4 w-4" /> No unmatched payments — everything reconciled.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => (
              <div
                key={r.payment_id}
                className="border border-amber-200 rounded-lg p-3 bg-white"
                data-testid={`unmatched-row-${r.payment_id}`}
              >
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600 mb-2">
                  <span className="flex items-center gap-1 font-mono text-slate-800">
                    <CreditCard className="h-3 w-3 text-slate-400" />{r.payment_id}
                  </span>
                  {typeof r.amount === 'number' && (
                    <span className="font-semibold text-slate-900">{activeCurrencySymbol()}{r.amount.toLocaleString('en-IN')}</span>
                  )}
                  {r.phone && (
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400" />{r.phone}</span>
                  )}
                  {r.email && (
                    <span className="flex items-center gap-1"><Mail className="h-3 w-3 text-slate-400" />{r.email}</span>
                  )}
                  {r.payment_link_id && (
                    <span className="flex items-center gap-1 font-mono"><Link2 className="h-3 w-3 text-slate-400" />{r.payment_link_id}</span>
                  )}
                  <DecisionBadge value={r.reason} />
                  <span className="text-slate-400">
                    {r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Input
                    className="h-8 max-w-xs text-xs"
                    placeholder="CRM lead id (uuid)"
                    value={leadIds[r.payment_id] || ''}
                    onChange={(e) => setLeadIds({ ...leadIds, [r.payment_id]: e.target.value })}
                    data-testid={`unmatched-lead-input-${r.payment_id}`}
                  />
                  <select
                    className="h-8 rounded-md border border-slate-200 text-xs px-2 bg-white"
                    value={leadTypes[r.payment_id] || ''}
                    onChange={(e) => setLeadTypes({ ...leadTypes, [r.payment_id]: e.target.value })}
                    data-testid={`unmatched-lead-type-${r.payment_id}`}
                  >
                    <option value="">Auto-detect</option>
                    <option value="lead">Inspection lead</option>
                    <option value="service_lead">Service lead</option>
                  </select>
                  <Button
                    size="sm"
                    className="h-8 bg-amber-600 hover:bg-amber-700"
                    disabled={mapping === r.payment_id}
                    onClick={() => handleMap(r.payment_id)}
                    data-testid={`unmatched-map-btn-${r.payment_id}`}
                  >
                    {mapping === r.payment_id
                      ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      : <Send className="h-3.5 w-3.5 mr-1" />}
                    Map & record
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
import { activeCurrencySymbol } from '@/utils/currency';
